/**
 * Token bridge: the ONLY place Radar's palette is written down, and the place it
 * turns into CSS. The values below were copied once from the old Drive
 * `@driving-permit/ui-tokens` package (2026-10-02) so that Radar no longer
 * depends on any Drive package. Hex values live here and nowhere else: this
 * module writes CSS custom properties onto `:root`, and every component then
 * references those variables through the Tailwind theme in `styles.css`
 * (e.g. `bg-surface-base`, `text-ink`) and never a hex literal.
 */
const color = {
  brand: { primary: "#FA4901", action: "#D43F05", actionPress: "#B83505", onLight: "#C8390A", onWhite: "#C8390A", ui: "#FA4901", subtle: "#FEF2EC" },
  surface: { base: "#E9EEF5", raised: "#FFFFFF", sunken: "#DDE4EE", inverse: "#20242E" },
  text: { primary: "#20242E", secondary: "#5B6371", tertiary: "#636872", inverse: "#FFFFFF", link: "#a94b2c" },
  correct: "#17794B",
  correctBg: "#E6F4ED",
  incorrect: "#B3261E",
  incorrectBg: "#FCEDEC",
  warning: "#8A6100",
  warningBg: "#FFF4E0",
  info: "#1F5F8B",
  infoBg: "#E8F1F8",
} as const;
const font = {
  display: '"Source Serif 4", Georgia, "Times New Roman", serif',
  body: '"Hanken Grotesk", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  mono: 'ui-monospace, "SF Mono", "Roboto Mono", monospace',
} as const;
const type = {
  display: { fontSize: 32 },
  title: { fontSize: 24 },
  heading: { fontSize: 19 },
  body: { fontSize: 16 },
  label: { fontSize: 14 },
  caption: { fontSize: 13 },
} as const;
const radius = { sm: 6, md: 10, lg: 16 } as const;
const space = { 4: 16 } as const;

/** Flatten the token objects into `--name: value` pairs for `:root`. */
export function tokenCssVariables(): Record<string, string> {
  return {
    "--gg-brand-primary": color.brand.primary,
    "--gg-brand-action": color.brand.action,
    "--gg-brand-action-press": color.brand.actionPress,
    "--gg-brand-on-light": color.brand.onLight,
    "--gg-brand-on-white": color.brand.onWhite,
    "--gg-brand-ui": color.brand.ui,
    "--gg-brand-subtle": color.brand.subtle,

    "--gg-surface-base": color.surface.base,
    "--gg-surface-raised": color.surface.raised,
    "--gg-surface-sunken": color.surface.sunken,
    "--gg-surface-inverse": color.surface.inverse,

    "--gg-ink": color.text.primary,
    "--gg-ink-secondary": color.text.secondary,
    "--gg-ink-tertiary": color.text.tertiary,
    "--gg-ink-inverse": color.text.inverse,
    "--gg-link": color.text.link,

    "--gg-correct": color.correct,
    "--gg-correct-bg": color.correctBg,
    "--gg-incorrect": color.incorrect,
    "--gg-incorrect-bg": color.incorrectBg,
    "--gg-warning": color.warning,
    "--gg-warning-bg": color.warningBg,
    "--gg-info": color.info,
    "--gg-info-bg": color.infoBg,

    "--gg-font-display": font.display,
    "--gg-font-body": font.body,
    "--gg-font-mono": font.mono,

    // Type scale (px). Admin is desktop-first but keeps the shared scale so
    // headings match the app's rhythm.
    "--gg-text-display": `${type.display.fontSize}px`,
    "--gg-text-title": `${type.title.fontSize}px`,
    "--gg-text-heading": `${type.heading.fontSize}px`,
    "--gg-text-body": `${type.body.fontSize}px`,
    "--gg-text-label": `${type.label.fontSize}px`,
    "--gg-text-caption": `${type.caption.fontSize}px`,

    "--gg-radius-sm": `${radius.sm}px`,
    "--gg-radius-md": `${radius.md}px`,
    "--gg-radius-lg": `${radius.lg}px`,

    "--gg-space-4": `${space[4]}px`,
  };
}

/** Apply the token variables to the document root. Called once at startup. */
export function installTokens(): void {
  const root = document.documentElement;
  for (const [name, value] of Object.entries(tokenCssVariables())) {
    root.style.setProperty(name, value);
  }
}
